import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2, LockKeyhole, LogOut, ShieldAlert } from 'lucide-react'
import { useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router'
import { toast } from 'sonner'
import { z } from 'zod'
import { Field } from '@/components/common/FormBits'
import { Brand } from '@/components/layout/Sidebar'
import { Button } from '@/components/ui/button'
import { PasswordInput, PasswordStrength } from '@/features/account/PasswordField'
import { PasswordOtpStep } from '@/features/account/PasswordOtpStep'
import { PASSWORD_RULES } from '@/features/account/passwordRules'
import { apiErrorMessage } from '@/i18n'
import { useAuth } from '@/lib/auth/AuthProvider'

/** Zorunlu parola değişimi (must_change_password). İsteğe bağlı değişim Hesabım › Güvenlik sekmesindedir. */
export function ChangePasswordPage() {
  const { t } = useTranslation()
  const { status, user, requestPasswordOtp, changePassword, logout } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const [pre, setPre] = useState<string | null>(null)
  const [otp, setOtp] = useState('')
  const [resending, setResending] = useState(false)
  const forced = !!user?.must_change_password

  const schema = z
    .object({
      current_password: z.string().min(1, t('auth.passwordRequired')),
      new_password: z.string().refine((v) => PASSWORD_RULES.every((r) => r.test(v)), t('account.errors.weak')),
      confirm: z.string().min(1, t('account.errors.confirm')),
    })
    .refine((v) => v.new_password === v.confirm, { message: t('auth.changePassword.mismatch'), path: ['confirm'] })

  const form = useForm({ resolver: zodResolver(schema), defaultValues: { current_password: '', new_password: '', confirm: '' } })
  const next = useWatch({ control: form.control, name: 'new_password' })
  const { errors, isSubmitting } = form.formState

  if (status === 'loading') return null
  if (status === 'anonymous') return <Navigate to="/login" replace />
  if (!forced) return <Navigate to="/account?tab=security" replace />

  return (
    <div className="flex min-h-dvh flex-col bg-muted/30 px-4 py-6 sm:px-8 sm:py-8">
      <Brand />
      <div className="flex flex-1 items-center justify-center py-8">
        <form
          noValidate
          className="w-full max-w-md space-y-5 rounded-2xl border bg-card p-5 shadow-card sm:p-8"
          onSubmit={form.handleSubmit(async (v) => {
            setError(null)
            try {
              if (!pre) {
                setPre(await requestPasswordOtp(v.current_password, v.new_password))
                return
              }
              if (!/^\d{6}$/.test(otp)) {
                setError(t('auth.otpInvalid'))
                return
              }
              await changePassword(pre, otp, v.new_password)
              toast.success(t('account.passwordChanged'))
            } catch (e) {
              setError(apiErrorMessage(e))
            }
          })}
        >
          <div className="space-y-3">
            <span className="flex size-11 items-center justify-center rounded-xl bg-warning/15 text-warning-strong">
              <ShieldAlert className="size-5" aria-hidden />
            </span>
            <div className="space-y-1">
              <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">{t('auth.changePassword.title')}</h1>
              <p className="text-sm text-muted-foreground">{t('auth.changePassword.subtitle')}</p>
            </div>
          </div>
          <Field id="cp-current" label={t('auth.changePassword.current')} error={errors.current_password?.message}>
            <PasswordInput id="cp-current" readOnly={!!pre} autoComplete="current-password" aria-invalid={!!errors.current_password} {...form.register('current_password')} />
          </Field>
          <Field id="cp-next" label={t('auth.changePassword.next')} error={errors.new_password?.message}>
            <PasswordInput id="cp-next" readOnly={!!pre} autoComplete="new-password" aria-invalid={!!errors.new_password} {...form.register('new_password')} />
          </Field>
          <PasswordStrength value={next} />
          <Field id="cp-confirm" label={t('auth.changePassword.confirm')} error={errors.confirm?.message}>
            <PasswordInput id="cp-confirm" readOnly={!!pre} autoComplete="new-password" aria-invalid={!!errors.confirm} {...form.register('confirm')} />
          </Field>
          {pre ? (
            <PasswordOtpStep
              id="cp-otp"
              value={otp}
              onChange={setOtp}
              resending={resending}
              onResend={() => {
                setResending(true)
                const v = form.getValues()
                requestPasswordOtp(v.current_password, v.new_password)
                  .then(setPre)
                  .catch((e: unknown) => setError(apiErrorMessage(e)))
                  .finally(() => setResending(false))
              }}
            />
          ) : null}
          {error ? (
            <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          ) : null}
          <Button type="submit" className="h-10 w-full" disabled={isSubmitting}>
            {isSubmitting ? <Loader2 className="animate-spin" /> : <LockKeyhole />}
            {pre ? t('auth.changePassword.submit') : t('account.sendCode')}
          </Button>
          <Button type="button" variant="ghost" className="w-full" onClick={() => void logout()}>
            <LogOut />
            {t('auth.signOut')}
          </Button>
        </form>
      </div>
    </div>
  )
}
